/**
 * Widget UI state machine.
 *
 * Steps: SERVICE → [ENHANCE?] → STAFF → TIME → CONTACT → [PAYMENT?] → CONFIRM
 * Plus DONE (final state with bookingId).
 *
 * ENHANCE is conditional — only inserted when the chosen base service has
 * at least one configured add-on or modifier (D55b). Silently skipped
 * otherwise, including the progress pip, so empty configs don't surface
 * a dead step.
 */

import type { AvailableSlot, Booking, BootstrapResponse, Cart, DepositInfo } from './types';

export type Step =
  | 'SERVICE'
  | 'ENHANCE'
  | 'STAFF'
  | 'TIME'
  | 'CONTACT'
  | 'PAYMENT'
  | 'CONFIRM'
  | 'DONE';

export interface WidgetState {
  step: Step;
  bootstrap: BootstrapResponse | null;
  cart: Cart | null;
  selectedServiceId: string | null;
  selectedStaffId: string | null;
  selectedDate: string | null;
  selectedSlot: AvailableSlot | null;
  availableDates: string[];
  availableSlots: AvailableSlot[];
  loading: boolean;
  error: string | null;
  booking: Booking | null;
  deposit: DepositInfo | null;
  // D71 — non-PAN summary of the card saved this session (sb-vault card-on-
  // file), so the Done step can say which card the salon will charge. Null
  // when no card was saved.
  attachedCard: { brand?: string; last4?: string } | null;
  // Phase 7 — D55b. Add-on picks ride into the cart as additional lines on
  // ENHANCE → STAFF transition. Modifier picks stay client-side and surface
  // on the Booking record after checkout (salon collects at appointment).
  selectedAddOnIds: string[];
  selectedModifierIds: string[];
  // Phase 7+. Active `?category=` deep-link filter. Filter is a snapshot
  // (id + name + serviceIds) so the Service step can render members in
  // the operator-defined order without re-resolving the bucket on every
  // render. Null = no filter; Service step renders the full menu.
  categoryFilter: {
    id: string;
    name: string;
    serviceIds: string[];
  } | null;
  // The cart is a hard prerequisite for every action after SERVICE, and it is
  // created exactly once, at mount. If that fails there is no retry path and
  // no `CART_CLEARED` — `cart: null` is sticky and terminal. Historically it
  // was also SILENT: bootstrap had already dispatched, so the catalogue
  // rendered, every button looked live, and every handler hit an
  // `if (!state.cart) return;` and did nothing. Forever. This flag makes that
  // state visible and recoverable. Deliberately NOT cleared by STEP_SET (which
  // wipes `error`) — the failure outlives any navigation.
  cartInitFailed: boolean;
}

export const initialState: WidgetState = {
  step: 'SERVICE',
  bootstrap: null,
  cart: null,
  cartInitFailed: false,
  selectedServiceId: null,
  selectedStaffId: null,
  selectedDate: null,
  selectedSlot: null,
  availableDates: [],
  availableSlots: [],
  loading: false,
  error: null,
  booking: null,
  deposit: null,
  attachedCard: null,
  selectedAddOnIds: [],
  selectedModifierIds: [],
  categoryFilter: null,
};

export type Action =
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; value: string | null }
  | { type: 'BOOTSTRAP_LOADED'; value: BootstrapResponse }
  | { type: 'FINANCING_REFRESHED'; methods: string[] | undefined }
  | { type: 'CART_SET'; value: Cart }
  | { type: 'CART_INIT_FAILED'; value: string }
  | { type: 'CART_INIT_RETRY' }
  | { type: 'STEP_SET'; value: Step }
  | { type: 'SERVICE_SELECTED'; serviceId: string }
  | { type: 'SERVICE_CLEARED' }
  | { type: 'STAFF_SELECTED'; staffId: string | null }
  | { type: 'DATE_SELECTED'; date: string }
  | { type: 'SLOT_SELECTED'; slot: AvailableSlot }
  | { type: 'TIME_SELECTION_CLEARED' }
  | { type: 'DATES_LOADED'; dates: string[] }
  | { type: 'SLOTS_LOADED'; slots: AvailableSlot[] }
  | { type: 'BOOKING_CONFIRMED'; booking: Booking }
  | { type: 'DEPOSIT_LOADED'; deposit: DepositInfo }
  | { type: 'CARD_ATTACHED'; card: { brand?: string; last4?: string } | null }
  | { type: 'ADDON_TOGGLED'; serviceId: string; groupName: string | null; siblings: string[] }
  | { type: 'MODIFIER_TOGGLED'; modifierId: string; groupName: string | null; siblings: string[] }
  | { type: 'UPSELLS_RESET' }
  | {
      type: 'CATEGORY_FILTER_SET';
      filter: WidgetState['categoryFilter'];
    };

export function reducer(state: WidgetState, action: Action): WidgetState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.value };
    case 'SET_ERROR':
      return { ...state, error: action.value, loading: false };
    case 'BOOTSTRAP_LOADED':
      return { ...state, bootstrap: action.value, loading: false };
    case 'FINANCING_REFRESHED':
      // P5.3 — copy only, never the catalogue: safe mid-cart.
      return state.bootstrap
        ? { ...state, bootstrap: { ...state.bootstrap, financingMethods: action.methods } }
        : state;
    case 'CART_SET': {
      // Clears cartInitFailed so a successful retry recovers the widget.
      // When the server demoted the hold underneath us (lazy expiry →
      // SLOT_EXPIRED, or a line edit invalidating the reservation →
      // SLOT_INVALIDATED), the stale date/slot selection must not survive:
      // the Time step renders selection from state.selectedSlot, so a
      // lingering value shows the old slot as still held with Continue
      // enabled — and the demote nulled expiresAt, so no countdown will
      // ever clear it. Same reset as TIME_SELECTION_CLEARED.
      const holdGone = (action.value.validationErrors ?? []).some(
        (v) => v.code === 'SLOT_EXPIRED' || v.code === 'SLOT_INVALIDATED',
      );
      if (holdGone && (state.selectedSlot !== null || state.selectedDate !== null)) {
        return {
          ...state,
          cart: action.value,
          cartInitFailed: false,
          selectedDate: null,
          selectedSlot: null,
          availableSlots: [],
        };
      }
      return { ...state, cart: action.value, cartInitFailed: false };
    }
    case 'CART_INIT_FAILED':
      return {
        ...state,
        cartInitFailed: true,
        error: action.value,
        loading: false,
      };
    case 'CART_INIT_RETRY':
      return { ...state, cartInitFailed: false, error: null, loading: true };
    case 'STEP_SET':
      return { ...state, step: action.value, error: null };
    case 'SERVICE_SELECTED':
      // Reset upsell picks when the base service changes — old picks no
      // longer apply to the new service's bag.
      return {
        ...state,
        selectedServiceId: action.serviceId,
        selectedAddOnIds: [],
        selectedModifierIds: [],
      };
    case 'SERVICE_CLEARED':
      // Used by the configurator when the user switches to a flat
      // family that doesn't contain the previous selection — without
      // clearing, Continue would submit the prior service while the
      // UI reads as a different family active.
      return {
        ...state,
        selectedServiceId: null,
        selectedAddOnIds: [],
        selectedModifierIds: [],
      };
    case 'ADDON_TOGGLED': {
      const has = state.selectedAddOnIds.includes(action.serviceId);
      // Single-select within a named group (e.g. Lotus's "Post Color" finish):
      // picking another sibling replaces the current pick, and re-clicking the
      // ALREADY-selected member is a no-op (radio semantics — you switch, you
      // don't clear; this keeps a required group from being emptied). Ungrouped
      // add-ons stack additively and toggle off on re-click (checkbox behavior).
      let next = state.selectedAddOnIds;
      if (action.groupName) {
        if (has) return state; // no-op — a radio pick can't be un-picked
        next = next.filter((id) => !action.siblings.includes(id));
        next = [...next, action.serviceId];
      } else if (has) {
        next = next.filter((id) => id !== action.serviceId);
      } else {
        next = [...next, action.serviceId];
      }
      return { ...state, selectedAddOnIds: next };
    }
    case 'MODIFIER_TOGGLED': {
      const has = state.selectedModifierIds.includes(action.modifierId);
      // Single-select within a named group: picking another sibling
      // replaces the current pick. Ungrouped modifiers stack additively.
      let next = state.selectedModifierIds;
      if (has) {
        next = next.filter((id) => id !== action.modifierId);
      } else if (action.groupName) {
        next = next.filter((id) => !action.siblings.includes(id));
        next = [...next, action.modifierId];
      } else {
        next = [...next, action.modifierId];
      }
      return { ...state, selectedModifierIds: next };
    }
    case 'UPSELLS_RESET':
      return { ...state, selectedAddOnIds: [], selectedModifierIds: [] };
    case 'CATEGORY_FILTER_SET':
      return { ...state, categoryFilter: action.filter };
    case 'STAFF_SELECTED':
      return { ...state, selectedStaffId: action.staffId };
    case 'DATE_SELECTED':
      // Clear stale slots + the previous selection so a date change
      // doesn't briefly render times from the old day or leave a slot
      // pill in the running bar that no longer matches.
      return {
        ...state,
        selectedDate: action.date,
        availableSlots: [],
        selectedSlot: null,
      };
    case 'SLOT_SELECTED':
      return { ...state, selectedSlot: action.slot };
    case 'TIME_SELECTION_CLEARED':
      // Slot-hold expiry (HoldStrip countdown or a server-side
      // CART_EXPIRED on checkout): the held slot is gone, so the stale
      // date/slot selection must not survive — a lingering selectedSlot
      // keeps the running bar's Continue enabled, and a lingering
      // selectedDate suppresses the Time step's first-date auto-pin.
      return {
        ...state,
        selectedDate: null,
        selectedSlot: null,
        availableSlots: [],
      };
    case 'DATES_LOADED':
      return { ...state, availableDates: action.dates, loading: false };
    case 'SLOTS_LOADED':
      return { ...state, availableSlots: action.slots, loading: false };
    case 'BOOKING_CONFIRMED':
      return { ...state, booking: action.booking, step: 'DONE', loading: false };
    case 'DEPOSIT_LOADED':
      return { ...state, deposit: action.deposit, loading: false };
    case 'CARD_ATTACHED':
      return { ...state, attachedCard: action.card };
    default:
      return state;
  }
}
